import { AppLogo } from "@/src/components/AppLogo";
import { HexagonIcon } from "@/src/components/icons";
import { useTranslations } from "@/src/i18n/i18n";
import { HexGrid } from "@/src/pages/SignIn/components/HexGrid";

/**
 * Ce que l'admin permet de faire — le même découpage que le tableau de bord.
 *
 * Ce sont des **suffixes de clés**, pas des phrases : la liste garde son ordre
 * ici, le texte vit dans le dictionnaire. Le gabarit `feature-${…}` reste
 * vérifié par `tsc`, qui refuse un suffixe sans clé correspondante.
 */
const FEATURES = ["reports", "events", "accounts"] as const;

/**
 * Colonne de gauche de la page de connexion.
 *
 * Purement décorative : masquée sous `lg`, où l'écran n'a de place que pour le
 * formulaire. Elle reste **sombre dans les deux thèmes** — c'est une surface de
 * marque, comme la couverture d'un produit, pas une surface d'interface. D'où
 * les couleurs écrites en dur plutôt que prises aux tokens HeroUI.
 */
export function BrandPanel() {
  const t = useTranslations("signIn");
  const tg = useTranslations("global");

  return (
    <section className="relative hidden min-w-0 overflow-hidden bg-brand-ink text-white lg:flex lg:flex-col lg:justify-between lg:p-12">
      {/* Halos de marque : violet en haut à gauche, ambre en bas à droite. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 -left-32 size-[30rem] rounded-full bg-brand/25 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-32 -bottom-40 size-[28rem] rounded-full bg-brand-amber/15 blur-3xl"
      />
      <HexGrid className="pointer-events-none absolute inset-0 size-full text-white/[0.06]" />

      <div className="relative flex items-center gap-3">
        <AppLogo className="size-10 rounded-xl" onDarkSurface />
        <span className="text-lg font-semibold tracking-tight">
          {tg("app-name")}
        </span>
      </div>

      <div className="relative max-w-md">
        <h2 className="text-4xl leading-[1.15] font-semibold tracking-tight text-balance">
          {t("tagline")}
        </h2>

        <ul className="mt-10 flex flex-col gap-4">
          {FEATURES.map((feature) => (
            <li className="flex items-center gap-3 text-white/70" key={feature}>
              <HexagonIcon className="size-3.5 shrink-0 text-brand" />
              {t(`feature-${feature}`)}
            </li>
          ))}
        </ul>
      </div>

      <p className="relative text-sm text-white/40">{t("footnote")}</p>
    </section>
  );
}
